import ReactDomServer from 'react-dom/server';

import { NameFirstInSubject } from '../helpers/email-defaults';
import {
  EmailTypeEnum,
  IScheduledEmailCustomizations,
  IScheduledEmailSettings,
  type IScheduledEmailContent,
} from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { Channel } from '@/types/channel.types';
import { LanguagesAbbr } from '@/types/translation.types';
import { getColorOptionsFromColor } from '@/utils/colors/get-color-options-from-color';
import { ColorOptions, ColorValueIndexes, EPaletteModes, SafeFonts } from '@/types/theme-packs.types';
import { SessionTypesEnum } from '@/types/session.types';
import { ScheduledEmail } from './create-email';
import { getCalendarLinks } from '../helpers/get-calendar-links';
import EmailWrapper from './email-wrapper';
import { sessionNameAndDescription } from '../helpers/session-name-and-description';
import { projectNameAndDescription } from '../helpers/project-name-and-description';
import { isCustomEmailV2 } from '../helpers/is-email-template-v1';

type TGetEmailToSendParams = {
  emailType: EmailTypeEnum;
  workingProject: BrandliveEvent;
  currentChannel: Channel;
  publishedUrl: string;
  emailContent: IScheduledEmailCustomizations;
  language?: LanguagesAbbr;
  sessionUUID?: string;
  emailSendTime?: number;
  timezone: Intl.DateTimeFormatOptions['timeZone'];
  emailSettings?: IScheduledEmailSettings;
  overrideSubject?: boolean;
  batchId?: string;
};

// complete email packaged as string to insert in db and pass on to email sender
export const getEmailToSend = async ({
  emailType,
  workingProject,
  currentChannel,
  publishedUrl,
  emailContent: _emailContent,
  language,
  sessionUUID,
  emailSendTime,
  timezone = 'America/Los_Angeles',
  emailSettings = {},
  overrideSubject = false,
  batchId,
}: TGetEmailToSendParams): Promise<IScheduledEmailCustomizations | undefined> => {
  let emailContent: IScheduledEmailCustomizations = { ..._emailContent };

  const paletteMode = workingProject.settings.email?.color_palette_mode ?? EPaletteModes.Light;
  const emailColorOptions = getColorOptionsFromColor(workingProject.settings.email?.colors, paletteMode);
  const eventColorOptions = getColorOptionsFromColor(workingProject.settings.design?.colors, paletteMode);
  const colors: ColorOptions = emailColorOptions ?? eventColorOptions;
  const fonts: SafeFonts = workingProject.settings.email?.font ?? SafeFonts.Arial;
  const session = sessionUUID ? workingProject?.sessions.find((session) => session.uuid === sessionUUID) : undefined;

  const emailLanguages = (): LanguagesAbbr[] => {
    if (language) {
      return [language];
    } else if (session) {
      return session.languages as LanguagesAbbr[];
    }
    return Array.from(new Set(workingProject.sessions.map((session) => session.languages as LanguagesAbbr[]).flat()));
  };

  const isAllOnDemand = () => {
    return !workingProject.sessions.find((session) => session.session_type !== SessionTypesEnum.onDemand);
  };

  const isOnDemandSession = () => {
    return session?.session_type === SessionTypesEnum.onDemand;
  };

  let anyChange = false;

  if (isCustomEmailV2(emailContent)) {
    return undefined;
  }

  await Promise.all(
    emailLanguages().map(async (language) => {
      const translationsImport = await import(`../../../../../../../i18n/${language}/email.json`);
      const translations = translationsImport?.default;

      const calendarLinks =
        emailSettings?.showAddCalendar && !isAllOnDemand()
          ? await getCalendarLinks({
              project: workingProject,
              channel: currentChannel,
              publishedUrl,
              language,
              translations,
              session,
              icsSettings: emailSettings.icsSettings,
              batchId,
            })
          : undefined;

      const jsx = (
        <ScheduledEmail
          emailType={emailType}
          language={language}
          workingProject={workingProject}
          currentChannel={currentChannel}
          publishedUrl={publishedUrl}
          emailTranslation={translations}
          sessionUUID={sessionUUID}
          emailSendTime={emailSendTime}
          emailToSend={true}
          customContent={emailContent[language] as IScheduledEmailContent}
          customSettings={emailSettings}
          calendarLinks={calendarLinks}
          timezone={timezone}
          hideCalendar={isAllOnDemand() || isOnDemandSession()}
        />
      );
      const html = ReactDomServer.renderToString(jsx);
      const emailBody = EmailWrapper(html, colors?.secondaryBackgroundColor?.[ColorValueIndexes.hex], fonts);
      const { name } = session
        ? sessionNameAndDescription(session, language)
        : projectNameAndDescription(workingProject, language);

      const subject =
        emailContent[language]?.subject ??
        (NameFirstInSubject.includes(emailType)
          ? `${name} ${translations[`subject.${emailType}`]}`
          : `${translations[`subject.${emailType}`]} ${name}`);

      const prevContent = (
        workingProject.registration_settings?.registration_confirmation_email?.[language] as
          | IScheduledEmailContent
          | undefined
      )?.base;
      if (
        prevContent !== emailBody ||
        workingProject.registration_settings?.registration_confirmation_email?.[language]?.subject !== subject
      ) {
        anyChange = true;
      }

      // @ts-expect-error the runtime type check should have prevented us from getting here, so we can assume this is right
      emailContent[language] = {
        ...emailContent[language],
        base: emailBody,
        subject: subject,
        icsContent: calendarLinks?.content,
        icsLink: calendarLinks?.iCal,
        googleLink: calendarLinks?.google,
      };

      // this needs to go AFTER we create the emailContent object
      // otherwise we won't:
      // 1. have the emailContent to override for newly added languages
      // 2. successfully override any subject for any language if the project name changes
      if (overrideSubject) {
        const updatedSubject = translations?.['subject.event_registration_email'];
        if (updatedSubject && language in emailContent) {
          // @ts-expect-error the runtime type check should have prevented us from getting here, so we can assume this is right
          emailContent = {
            ...emailContent,
            [language]: {
              ...emailContent[language],
              subject: `${updatedSubject} ${workingProject.name}`,
            },
          };
        }
      }
    })
  );

  if (anyChange) {
    return emailContent;
  } else {
    return undefined;
  }
};

export default getEmailToSend;
